import {
  ArrowLeftRight,
  ArrowRightLeft,
  Banknote,
  BadgeCheck,
  CalendarDays,
  CalendarHeart,
  ClipboardCheck,
  Clock4,
  FileEdit,
  Landmark,
  ListChecks,
  MapPinOff,
  MessageSquareQuote,
  NotebookPen,
  OctagonAlert,
  Package,
  Receipt,
  ScrollText,
  Smile,
  Timer,
  Trophy,
  UserMinus,
  UserSquare,
  type LucideIcon,
} from 'lucide-react';
import type { PendingActionSource } from '../../api/pendingActions';
import { parseApiUtc, parseApiDateOnly } from '@/utils/apiDate';
import { appDate, appMonthNameYear } from '@/utils/appDateFormat';

// Presentation metadata per source. `label` is a flat key in the `requests`
// i18n namespace (keySeparator is false — literal strings).

interface SourceMeta {
  icon: LucideIcon;
  label: string;
}

const FALLBACK: SourceMeta = { icon: ClipboardCheck, label: 'Request' };

const SOURCE_META: Record<PendingActionSource, SourceMeta> = {
  Leave: { icon: CalendarDays, label: 'Leave' },
  ShiftSwap: { icon: ArrowLeftRight, label: 'Shift swap' },
  Claim: { icon: Receipt, label: 'Claim' },
  AttendanceCorrection: { icon: FileEdit, label: 'Correction' },
  ShortLeave: { icon: Timer, label: 'Short leave' },
  Encashment: { icon: Banknote, label: 'Encashment' },
  AssetRequest: { icon: Package, label: 'Asset' },
  ShiftSwapAcceptance: { icon: ArrowLeftRight, label: 'Swap for you' },
  Onboarding: { icon: ListChecks, label: 'Onboarding' },
  Terms: { icon: ScrollText, label: 'Terms' },
  Survey: { icon: Smile, label: 'Pulse survey' },
  ReturnToWork: { icon: ClipboardCheck, label: 'Return to work' },
  Referee: { icon: Trophy, label: 'Referee' },
  OutOfBoundsClock: { icon: MapPinOff, label: 'Out of bounds' },
  // Approval-wave sources. Keep this a TOTAL Record: a new member of the
  // PendingActionSource union must break the build here, not fall back to a
  // generic clipboard the user can't tell apart from a real one.
  InternalMovement: { icon: ArrowRightLeft, label: 'Internal movement' },
  EmployeeMovement: { icon: ArrowRightLeft, label: 'Employee movement' },
  PublicProfile: { icon: UserSquare, label: 'Public profile' },
  GpssaPending: { icon: Landmark, label: 'GPSSA contributions' },
  GoalBlocker: { icon: OctagonAlert, label: 'Goal blocker' },
  ReflectionResponse: { icon: MessageSquareQuote, label: 'Reflection response' },
  OvertimeApproval: { icon: Timer, label: 'Overtime' },
  ShiftPayApproval: { icon: Clock4, label: 'Hourly shift' },
  Offboarding: { icon: UserMinus, label: 'Offboarding' },
  PlatformTerms: { icon: ScrollText, label: 'Platform terms' },
  Reflection: { icon: NotebookPen, label: 'Reflection' },
  PerformanceSnapshot: { icon: BadgeCheck, label: 'Performance snapshot' },
  RestrictedHoliday: { icon: CalendarHeart, label: 'Restricted holidays' },
};

/**
 * Titles the server writes as FIXED English strings (PendingActionsService: "Short leave", "Shift swap", ...).
 * These are system text, so they localize on the client. Every other title is tenant data (leave type name,
 * claim type, movement type) and is shown as sent. Keep in step with the literal `Title = "..."` assignments in
 * Mowazaf.Server/Services/Dashboard/PendingActionsService.cs; each entry is a flat key in `requests`.
 */
const FIXED_SERVER_TITLES = new Set<string>([
  'Shift swap', 'Attendance correction', 'Out-of-bounds clock', 'Overtime approval', 'Short leave',
  'Leave encashment', 'Asset request', 'Public profile', 'GPSSA contributions', 'Reflection response',
  'Goal blocker', 'Offboarding tasks', 'Platform terms', 'Monthly reflection', 'Performance snapshot',
  'Restricted holidays', 'Shift swap request', 'Onboarding tasks', 'Terms & policies', 'Monthly pulse',
  'Return to work', 'Referee assignment',
]);

/** The row title in the viewer's language: fixed server titles translated, tenant data untouched. */
export function localizedTitle(item: { source: string; title: string }, t: TFunc): string {
  if (item.source === 'Survey') return t('Monthly pulse');
  return FIXED_SERVER_TITLES.has(item.title) ? t(item.title) : item.title;
}

/**
 * CO-816 — the inbox's subtitle previously showed `PendingActionsService`'s system-generated
 * sentences ("1 task left", "Shift cover", "Updated terms need your acceptance", "Clock-out
 * outside the allowed area", "{{team1}} vs {{team2}}", …) verbatim: always English, on every
 * locale and on both clients (this is the SAME defect on mobile — Flutter has no client-side
 * builder to paper over it the way `leaveSubtitle` already did for Leave here).
 *
 * Fixed at the SOURCE this time, not just the web client: `PendingActionItem` (server) now carries
 * `SubtitleKey` + `SubtitleArgs` alongside the unchanged `Subtitle` — a fully-qualified i18next key
 * ("translation:<literal English sentence>") plus its interpolation params, computed once on the
 * server from data it already had, and consumed identically by web and mobile. `Subtitle` itself
 * is UNCHANGED, so an app build that predates this field (older mobile release, a mid-rollout
 * server) keeps rendering exactly what it did before — never a raw key, never nothing.
 *
 * `translation.json` (not `requests.json`) is where the sentences live: mobile's namespace merge
 * (`local.dart`) fetches a FIXED `['tournament','translation']` pair and nothing else, so a key
 * anywhere else — including this repo's own `requests` namespace — is unreachable from the phone
 * (see CO-784). One home for these sentences, reachable by both clients, is the point.
 */
export function localizedSubtitle(
  item: { source: string; subtitle: string; subtitleKey?: string; subtitleArgs?: Record<string, string>; meta?: Record<string, string> },
  t: TFunc
): string {
  if (!item.subtitleKey) return item.subtitle;
  // GpssaPending is the one shape that needs client-side WORK, not just interpolation: the server
  // deliberately withholds a pre-formatted month name (that would be English prose smuggled through
  // a param — the exact "invisible i18n defect" this fix exists to avoid) and sends raw `year`/
  // `month` on `Meta` instead, same as `leaveSubtitle` already formats Leave's dates client-side.
  let args = item.subtitleArgs;
  if (item.source === 'GpssaPending') {
    const year = Number(item.meta?.year);
    const month = Number(item.meta?.month);
    const monthLabel = Number.isFinite(year) && Number.isFinite(month) ? appMonthNameYear(new Date(year, month - 1, 1), 'long') : '';
    if (!monthLabel) return item.subtitle;
    args = { ...args, month: monthLabel };
  }
  // CO-816 review — plurals. The server sends `count` as a STRING (SubtitleArgs is string->string),
  // and i18next skips plural resolution entirely when `count` is a string, so Arabic would always get
  // the unsuffixed form ("2 مهام" instead of "مهمتان"). Hand it a number so `_one/_two/_few/_many/_other`
  // resolve; the unsuffixed key stays as the fallback for a locale without the plural variants.
  const opts: Record<string, unknown> = { ...args };
  if (typeof args?.count === 'string') {
    const n = Number(args.count);
    if (Number.isFinite(n)) opts.count = n;
    else delete opts.count;
  }
  // `defaultValue` is the same safety net as `serverErrorCodeKeys.ts`: a key this bundle cannot
  // resolve (a locale gap, a source added after this build) degrades to the server's English
  // subtitle rather than painting a raw i18next key at the user.
  return t(item.subtitleKey, { ...opts, defaultValue: item.subtitle });
}

export function sourceMeta(source: string): SourceMeta {
  return SOURCE_META[source as PendingActionSource] ?? FALLBACK;
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

/** True when two dates fall on the same calendar day (local time). */
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Compact date span in the system format ("19-07-2026" / "19-07-2026 – 20-07-2026"), mirroring the
 * server's cases. dd-mm-yyyy for every viewer (owner decision 2026-09-17), never the machine locale.
 */
function formatDateRange(startIso: string | undefined, endIso: string | undefined, _locale: string): string | undefined {
  if (!startIso) return undefined;
  // Calendar days (leave spans) — read textually; `new Date('2026-09-17')` is the previous day west of UTC.
  const start = parseApiDateOnly(startIso);
  if (!start) return undefined;
  const end = endIso ? parseApiDateOnly(endIso) : null;
  if (!end || Number.isNaN(end.getTime()) || isSameDay(start, end)) {
    return appDate(start);
  }
  return `${appDate(start)} – ${appDate(end)}`;
}

/**
 * The system-generated part of a Leave item's subtitle ("2 days · 19–20 Jul"),
 * localized on the client from the item's structured Meta (numberOfDays /
 * startDate / endDate). The leave TYPE is user data and stays server-provided
 * (the row's title). Returns undefined when the Meta is missing so the caller
 * can fall back to the server's English subtitle.
 */
export function leaveSubtitle(meta: Record<string, string> | undefined, locale: string, t: TFunc): string | undefined {
  if (!meta) return undefined;
  const range = formatDateRange(meta.startDate, meta.endDate, locale);
  if (!range) return undefined;
  const days = Number(meta.numberOfDays);
  const daysLabel =
    Number.isFinite(days) && days > 0
      ? days === 1
        ? t('1 day')
        : t('{{n}} days', { n: days.toLocaleString(locale) })
      : undefined;
  return daysLabel ? `${daysLabel} · ${range}` : range;
}

/**
 * Compact relative timestamp ("2m ago" / "3h ago" / "5d ago", older falls back
 * to a dd-mm-yyyy date). Mirrors the mobile inbox so both clients read the same.
 */
export function relativeTime(iso: string | undefined, locale: string, t: TFunc): string {
  if (!iso) return '';
  const when = parseApiUtc(iso);
  if (!when) return '';
  const diffMs = Date.now() - when.getTime();
  const minutes = Math.floor(diffMs / 60000);
  // {{n}} (not {{count}}) on purpose: `count` triggers i18next plural-suffix
  // lookups these flat keys don't provide.
  if (minutes < 1) return t('Just now');
  if (minutes < 60) return t('{{n}}m ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{{n}}h ago', { n: hours });
  const days = Math.floor(hours / 24);
  if (days <= 7) return t('{{n}}d ago', { n: days });
  return appDate(when);
}
